import { Inject, Injectable } from '@angular/core';
import { AuthService, User } from '@auth0/auth0-angular';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ILdRequestContext } from './feature-flag-model';
import { createUserContext } from './feature-flag-utils';
import { isNullish } from '@utils/is-nullish.tool';
import { APP_CONFIG, AppConfiguration } from '@app/infrastructure/auth/app-configuration';
import { CustomerType } from '@partners/customer-type.enum';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';

@Injectable({
	providedIn: 'root',
})
export class FeatureFlagManagerService {
	private readonly queryParamsFeatureFlagsKey = 'feature';
	private _ldContext: ILdRequestContext;
	private _ldClient: LaunchDarkly.LDClient;
	private _flagsSubject = new BehaviorSubject<LaunchDarkly.LDFlagSet>({});

	constructor(
		@Inject(APP_CONFIG)
		private appConfig: AppConfiguration,
		private auth0: AuthService,
	) {
		this.initFeatureFlagSystem();
	}

	public get flags$(): Observable<LDFlagSet> {
		return this._flagsSubject.asObservable();
	}

	public get flags(): LDFlagSet {
		return this._flagsSubject.getValue();
	}

	private get hasFFsInQueryParams(): boolean {
		const queryParams = new URLSearchParams(window.location.search);

		return queryParams.has(this.queryParamsFeatureFlagsKey);
	}

	public initFeatureFlagSystem(): void {
		const sdkKey = this.appConfig.featureFlag.LAUNCHDARKLY_SDK_KEY;
		this.auth0.user$.pipe(take(1)).subscribe(
			(user: User) => {
				if (user) {
					try {
						this._ldContext = createUserContext(user, this.appConfig.apiUrl);
						window.zestyHost.user = { ...user, ...this._ldContext.user };
						this._ldClient = LaunchDarkly.initialize(sdkKey, this._ldContext);
						this._ldClient.on('ready', () => {
							this.updateFlags();
							this._ldClient.on('change', () => this.updateFlags());
						});
						this._ldClient.on('failed', () => {
							console.log(`launchdarkly failed to initialize`);
						});
					} catch (error) {
						console.log(`launchdarkly service error`, error);
					}
				}
			},
			(error) => {
				console.log(`auth0 error, feature flag service`, error);
			},
		);
	}

	public isFlagEnabled(flagName: string): boolean {
		return this.flags[flagName];
	}

	private parseQueryParamsFFs(ldFlags: LDFlagSet): LDFlagSet {
		const queryParams = new URLSearchParams(window.location.search);
		return queryParams
			.get(this.queryParamsFeatureFlagsKey)
			.split(',')
			.reduce((flags, curr) => {
				const [key, value] = curr.split(':');
				flags[key] = JSON.parse(value);
				// handle FF with complex data type
				if (typeof ldFlags[key] === 'object') {
					flags[key] = {
						...ldFlags[key],
						isActive: JSON.parse(value),
					};
				}

				return flags;
			}, {} as LDFlagSet);
	}

	private updateFlags(): void {
		const ldFlags = this._ldClient.allFlags();
		const parsedQueryParamsFFs = this.hasFFsInQueryParams ? this.parseQueryParamsFFs(ldFlags) : {};
		const isAllFlagsEnabled = localStorage.getItem('isAllFlagsEnabled');
		// update FFs via localStorage & queryParams
		for (let flag in ldFlags) {
			const lsFlag = localStorage.getItem(flag);
			if (lsFlag) {
				ldFlags[flag] = JSON.parse(lsFlag);
			}
			if (isAllFlagsEnabled) {
				ldFlags[flag] = true;
			}
			if (!isNullish(parsedQueryParamsFFs[flag])) {
				ldFlags[flag] = parsedQueryParamsFFs[flag];
			}
		}

		//close opt-hub flag for BO
		if (this.appConfig.customerType === CustomerType.BO) {
			ldFlags[FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB] = false;
		}

		if (window.zestyHost) {
			window.zestyHost.featureFlags = ldFlags;
		}
		this._flagsSubject.next(ldFlags);
	}
}
